import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar, 
  Clock, 
  CheckCircle2,
  Sun,
  Moon,
  Coffee,
  Users,
  Target,
  Lightbulb,
  Upload,
  Download,
  FileText,
  Trash2,
  Loader2,
  File,
  Image,
  FileVideo,
  FileAudio,
  Edit3,
  Save,
  X,
  RotateCcw,
  Plus,
  Eye,
  ExternalLink
} from 'lucide-react';
import { tagesplanungAPI } from '../api';
import { useAuth } from '../context/AuthContext';

// Complete training schedule data (15 days / 2 weeks)
const TAGESPLANUNG = [
  {
    tag: 1,
    name: "Sonntag",
    woche: 1,
    zeitbloecke: [
      {
        zeit: "17:00 - 18:00",
        thema: "Anreise",
        icon: "users",
        details: [
          "Leute kommen an - Ankommen lassen",
          "Aufnahmen machen vom Gespräch (Pitch, Einwandbehandlung)",
          "Zusammen in günstigem Lokal essen & Kennenlernrunde",
          "Klamotten vergeben & Klamottenliste führen",
          "Tablets einrichten und aufladen lassen",
          "Neuen: Reflexionsaufgabe bearbeiten, Ehrenkodex unterschreiben",
          "Gebiete einteilen (schwierigerer Part zuerst)",
          "Wochenplan erstellen"
        ]
      }
    ]
  },
  {
    tag: 2,
    name: "Montag",
    woche: 1,
    zeitbloecke: [
      {
        zeit: "Morgens",
        thema: "KV-Termin & Feldarbeit",
        icon: "coffee",
        details: [
          "Pünktlich beim KV-Termin sein (!)",
          "Neuwerbern am besten sagen, dass DU die Fragen stellst, nicht sie (sonst stellen die manchmal komische Fragen)",
          "Notizen beim KV-Termin machen; Neuwerbern sagen, sie sollen auch Notizen machen, gute Fragen stellen und die Notizen dann in die Gruppe schicken",
          "Nach dem KV-Termin Fundr erklären, alles zeigen - den Neuen einmal zeigen, wie du mit den Bürgern durch das Formular gehst",
          "Gebiet eintragen mit einem kleinen Teil, wo du mit denen zusammengehst; deren Gebiete sollten rundherum sein, sodass sie direkt danach in ihr Gebiet laufen können",
          "Danach die Neuen in Gruppen aufteilen (bei 7 z.B. 3, 2, 2)",
          "Die Stärksten zuerst, gefolgt von den Schwächeren (die Schwächeren sollen währenddessen lernen und sich gegenseitig pitchen)",
          "Während du mit den Leuten an die Tür gehst: 2-3 Gespräche vormachen und sie jeweils einmal pitchen lassen, damit sie reinkommen - danach gehen sie in ihre eigenen Gebiete und legen los",
          "100-Einheiten-Challenge in die Gruppe schicken",
          "Entscheiden: Entweder du kaufst für die Leute ein und machst Wochenkosten, oder du gehst am Abend mit ihnen zum Supermarkt und sie kaufen für sich ein (nur Frühstück und Mittagessen - für Abendessen ist ja gesorgt)",
          "Falls die Leute danach noch etwas brauchen, kannst du trotzdem Kleinigkeiten holen",
          "Ich lasse sie immer bis 20 Uhr gehen - dann schicken sie ihre Live-Location in die Gruppe und du holst sie ab"
        ]
      },
      {
        zeit: "Abends",
        thema: "Schulung: Lowperformerbogen & Unterbrechungen",
        icon: "moon",
        details: [
          "Gemeinsam den Lowperformerbogen durchgehen (erst fragen, was die Leute glauben, was einen Lowperformer ausmacht, dann schrittweise die Punkte durchgehen)",
          "Danach fragen, welche Unterbrechungen es gibt und was man da tut",
          "Erklären, wie man mit den einzelnen Unterbrechungen umgeht",
          "WICHTIG: Sie müssen lernen, den Pitch erstmal fertigzubringen! Die Neuen kommen sonst nie zur Aufforderung - das ist quasi Lektion Nummer 1! Wichtige Punkte gehen im Gespräch sonst verloren",
          "Lowperformerbogen und Unterbrechungstabelle nochmal in die Gruppe senden"
        ]
      },
      {
        zeit: "Tagesabschluss",
        thema: "Reflexion",
        icon: "target",
        details: [
          "Leute Tagebuch ausfüllen lassen",
          "Leute einzeln fragen nach Problemen",
          "Für morgen etwas vorbereiten basierend auf Feedback"
        ]
      }
    ]
  },
  {
    tag: 3,
    name: "Dienstag",
    woche: 1,
    zeitbloecke: [
      {
        zeit: "Morgens",
        thema: "Trockentraining: Pitch, Einwand, Beitragsgespräch",
        icon: "coffee",
        details: [
          "Trockentraining mit Fokus auf Unterbrechungen"
        ]
      },
      {
        zeit: "Abends",
        thema: "Schulung: Einwandbehandlung & Förderstimme",
        icon: "moon",
        details: [
          "Leute machen Einwandbehandlung-Strukturaufgabe",
          "Struktur der Einwandbehandlung erklären",
          "Förderstimme erklären (Audio zeigen und analysieren)"
        ]
      },
      {
        zeit: "Tagesabschluss",
        thema: "Reflexion",
        icon: "target",
        details: [
          "Leute Tagebuch ausfüllen lassen",
          "Leute einzeln fragen nach Problemen"
        ]
      }
    ]
  },
  {
    tag: 4,
    name: "Mittwoch",
    woche: 1,
    zeitbloecke: [
      {
        zeit: "Morgens",
        thema: "Trockentraining: Unterbrechungen & Förderstimme",
        icon: "coffee",
        details: [
          "Mit Trainees zusammengehen"
        ]
      },
      {
        zeit: "Abends",
        thema: "Schulung: Erstaufnahme & Einwandbehandlungen",
        icon: "moon",
        details: [
          "Einwandbehandlungen in eigenen Worten aufschreiben",
          "Leute sollen verstehen WARUM die Einwandbehandlungen funktionieren",
          "Erstaufnahme erklären (Audio zeigen und analysieren)"
        ]
      },
      {
        zeit: "Tagesabschluss",
        thema: "Reflexion",
        icon: "target",
        details: [
          "Leute Tagebuch ausfüllen lassen",
          "Leute einzeln fragen nach Problemen"
        ]
      }
    ]
  },
  {
    tag: 5,
    name: "Donnerstag",
    woche: 1,
    zeitbloecke: [
      {
        zeit: "Morgens",
        thema: "Trockentraining: Unterbrechungen & Erstaufnahme",
        icon: "coffee",
        details: [
          "Mit Trainees zusammengehen"
        ]
      },
      {
        zeit: "Abends",
        thema: "Schulung: Pro-Orgas",
        icon: "moon",
        details: [
          "Relevante Projekte erklären (auch aus anderen KVs)",
          "Was macht die Projekte stark?",
          "Storyboard-Aufgabe: Projekte aussuchen, die überzeugen"
        ]
      },
      {
        zeit: "Tagesabschluss",
        thema: "Reflexion",
        icon: "target",
        details: [
          "Leute Tagebuch ausfüllen lassen",
          "Leute einzeln fragen nach Problemen"
        ]
      }
    ]
  },
  {
    tag: 6,
    name: "Freitag",
    woche: 1,
    zeitbloecke: [
      {
        zeit: "Morgens",
        thema: "Trockentraining: Unterbrechungen & Pro-Orgas",
        icon: "coffee",
        details: [
          "Frei / Feldarbeit"
        ]
      },
      {
        zeit: "Abends",
        thema: "Schulung: Körpersprache, Website-Move, IBAN",
        icon: "moon",
        details: [
          "Leute schreiben Pitch komplett aus dem Kopf auf",
          "Körpersprache erklären - worauf achten?",
          "Website-Move erklären",
          "Wie komme ich zur IBAN? Was bei Widerstand?"
        ]
      },
      {
        zeit: "Tagesabschluss",
        thema: "Reflexion",
        icon: "target",
        details: [
          "Leute Tagebuch ausfüllen lassen",
          "Leute einzeln fragen nach Problemen"
        ]
      }
    ]
  },
  {
    tag: 7,
    name: "Samstag",
    woche: 1,
    zeitbloecke: [
      {
        zeit: "Morgens",
        thema: "Trockentraining: Unterbrechungen & Website-Move",
        icon: "coffee",
        details: [
          "Frei / Feldarbeit"
        ]
      },
      {
        zeit: "Abends",
        thema: "Wochenende",
        icon: "sun",
        details: [
          "Erhöhungsgespräch in die Gruppe schicken - muss gelernt werden!",
          "Gemeinsam essen gehen"
        ]
      }
    ]
  },
  {
    tag: 8,
    name: "Sonntag",
    woche: 1,
    zeitbloecke: [
      {
        zeit: "12:00 Uhr",
        thema: "Call mit Tobias (1 Stunde)",
        icon: "users",
        details: [
          "Handout von Tobias direkt ausfüllen lassen",
          "Erhöhungsgespräch lernen",
          "Free-Time",
          "Optional: Bill-Porter-Reportage anschauen"
        ]
      }
    ]
  },
  {
    tag: 9,
    name: "Montag",
    woche: 2,
    zeitbloecke: [
      {
        zeit: "Morgens",
        thema: "Trockentraining: Wissen auffrischen",
        icon: "coffee",
        details: [
          "20-Mitglieder-Challenge in die Gruppe schicken"
        ]
      },
      {
        zeit: "Abends",
        thema: "Schulung: Beitragsgespräch & Partnersplitting",
        icon: "moon",
        details: [
          "Leute schreiben das Beitragsgespräch aus dem Kopf runter",
          "Beitragsgespräch erklären – was wichtig ist etc."
        ]
      },
      {
        zeit: "Tagesabschluss",
        thema: "Reflexion",
        icon: "target",
        details: [
          "Leute Tagebuch ausfüllen lassen",
          "Leute einzeln fragen nach Problemen"
        ]
      }
    ]
  },
  {
    tag: 10,
    name: "Dienstag",
    woche: 2,
    zeitbloecke: [
      {
        zeit: "Morgens",
        thema: "Trockentraining: Pitch, Einwand, Beitragsgespräch",
        icon: "coffee",
        details: [
          "Trockentraining (Pitch, Random Einwand, Beitragsgespräch)"
        ]
      },
      {
        zeit: "Abends",
        thema: "Schulung: Namedropping",
        icon: "moon",
        details: [
          "Namedropping-Sheet mit den Leuten durchgehen"
        ]
      },
      {
        zeit: "Tagesabschluss",
        thema: "Reflexion",
        icon: "target",
        details: [
          "Leute Tagebuch ausfüllen lassen",
          "Leute einzeln fragen nach Problemen"
        ]
      }
    ]
  },
  {
    tag: 11,
    name: "Mittwoch",
    woche: 2,
    zeitbloecke: [
      {
        zeit: "Morgens",
        thema: "Trockentraining: Erhöhungsgespräch",
        icon: "coffee",
        details: [
          "Mit Trainees zusammengehen"
        ]
      },
      {
        zeit: "Abends",
        thema: "Schulung: Stornoabsicherung",
        icon: "moon",
        details: [
          "Stornoabsicherungs-Sheet durchgehen",
          "Worauf sollte man achten?",
          "Optional: Podcast anhören & analysieren (was hat Fundraiser falsch gemacht?)"
        ]
      },
      {
        zeit: "Tagesabschluss",
        thema: "Reflexion",
        icon: "target",
        details: [
          "Leute Tagebuch ausfüllen lassen",
          "Leute einzeln fragen nach Problemen"
        ]
      }
    ]
  },
  {
    tag: 12,
    name: "Donnerstag",
    woche: 2,
    zeitbloecke: [
      {
        zeit: "Morgens",
        thema: "Trockentraining: Beitragsgespräch & Stornoabsicherung",
        icon: "coffee",
        details: [
          "Trockentraining (Beitragsgespräch & Stornoabsicherung)"
        ]
      },
      {
        zeit: "Abends",
        thema: "Härteprüfung",
        icon: "target",
        details: [
          "Leute machen: Unterbrechungen, Einwandbehandlungen, Beitragsgespräch",
          "NACHHER-VIDEO aufnehmen!",
          "Kann auch am nächsten Morgen gemacht werden"
        ]
      },
      {
        zeit: "Tagesabschluss",
        thema: "Reflexion",
        icon: "target",
        details: [
          "Leute Tagebuch ausfüllen lassen",
          "Leute einzeln fragen nach Problemen"
        ]
      }
    ]
  },
  {
    tag: 13,
    name: "Freitag",
    woche: 2,
    zeitbloecke: [
      {
        zeit: "Abends",
        thema: "Schulung: Was macht einen Highperformer aus?",
        icon: "lightbulb",
        details: [
          "Optional: Audio von Orlando anhören"
        ]
      },
      {
        zeit: "Tagesabschluss",
        thema: "Reflexion",
        icon: "target",
        details: [
          "Leute Tagebuch ausfüllen lassen",
          "Leute einzeln fragen nach Problemen"
        ]
      }
    ]
  },
  {
    tag: 14,
    name: "Samstag",
    woche: 2,
    zeitbloecke: [
      {
        zeit: "Morgens",
        thema: "Trockentraining: Individuelles Coaching",
        icon: "coffee",
        details: [
          "Auf jeden einzeln eingehen",
          "Fokus auf individuelle Struggles"
        ]
      },
      {
        zeit: "Abends",
        thema: "Wochenende & Abschluss",
        icon: "sun",
        details: [
          "Frei",
          "Gemeinsam essen gehen",
          "Feedback-Fragebogen ausfüllen lassen"
        ]
      }
    ]
  },
  {
    tag: 15,
    name: "Sonntag",
    woche: 2,
    zeitbloecke: [
      {
        zeit: "Vormittags",
        thema: "Abschluss & Abreise",
        icon: "check",
        details: [
          "Unterkunft sauber machen",
          "Zertifikate generieren",
          "Abreise"
        ]
      }
    ]
  }
];

const getIcon = (iconName) => {
  const iconClass = "w-5 h-5";
  switch (iconName) {
    case 'coffee': return <Coffee className={iconClass} />;
    case 'moon': return <Moon className={iconClass} />;
    case 'sun': return <Sun className={iconClass} />;
    case 'users': return <Users className={iconClass} />;
    case 'target': return <Target className={iconClass} />;
    case 'lightbulb': return <Lightbulb className={iconClass} />;
    case 'check': return <CheckCircle2 className={iconClass} />;
    default: return <Clock className={iconClass} />;
  }
};

const getFileIcon = (contentType) => {
  if (contentType?.startsWith('image/')) return <Image className="w-4 h-4" />;
  if (contentType?.startsWith('video/')) return <FileVideo className="w-4 h-4" />;
  if (contentType?.startsWith('audio/')) return <FileAudio className="w-4 h-4" />;
  if (contentType?.includes('pdf')) return <FileText className="w-4 h-4" />;
  return <File className="w-4 h-4" />;
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const TagesplanungView = ({ open, onClose }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [currentDay, setCurrentDay] = useState(1);
  const [files, setFiles] = useState({});
  const [uploading, setUploading] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const fileInputRef = useRef(null);
  
  // Edit mode states
  const [editMode, setEditMode] = useState(false);
  const [customContent, setCustomContent] = useState({});
  const [editingContent, setEditingContent] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Preview state
  const [previewFile, setPreviewFile] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Get the current plan - check for custom content first
  const defaultPlan = TAGESPLANUNG.find(p => p.tag === currentDay);
  const customPlanContent = customContent[currentDay];
  
  const currentPlan = customPlanContent?.zeitbloecke?.length > 0 
    ? { ...defaultPlan, zeitbloecke: customPlanContent.zeitbloecke }
    : defaultPlan;
    
  const currentDayFiles = files[currentDay] || [];
  const hasCustomContent = customContent[currentDay]?.zeitbloecke?.length > 0;
  
  // Load files and custom content when modal opens
  useEffect(() => {
    if (open) {
      loadAllFiles();
      loadAllCustomContent();
    }
  }, [open]);
  
  const loadAllCustomContent = async () => {
    try {
      const allContent = await tagesplanungAPI.getAllContent();
      const grouped = {};
      allContent.forEach(item => {
        grouped[item.day] = item;
      });
      setCustomContent(grouped);
    } catch (error) {
      console.error('Error loading custom content:', error);
    }
  };
  
  const loadAllFiles = async () => {
    setLoadingFiles(true);
    try {
      const allFiles = await tagesplanungAPI.getAllFiles();
      // Group files by day
      const grouped = {};
      allFiles.forEach(file => {
        if (!grouped[file.day]) grouped[file.day] = [];
        grouped[file.day].push(file);
      });
      setFiles(grouped);
    } catch (error) {
      console.error('Error loading files:', error);
    }
    setLoadingFiles(false);
  };
  
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      await tagesplanungAPI.uploadFile(currentDay, file);
      await loadAllFiles();
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Fehler beim Hochladen: ' + (error.response?.data?.detail || error.message));
    }
    setUploading(false);
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Datei wirklich löschen?')) return;
    
    try {
      await tagesplanungAPI.deleteFile(currentDay, fileId);
      await loadAllFiles();
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Fehler beim Löschen: ' + (error.response?.data?.detail || error.message));
    }
  };
  
  const handleDownload = async (file) => {
    const url = tagesplanungAPI.getDownloadUrl(file.day, file.id);
    
    try {
      const response = await fetch(url, { credentials: 'include' });
      
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = file.original_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Fehler beim Download');
    }
  };
  
  // Check if file can be previewed (images, PDFs and audio)
  const canPreview = (file) => {
    const type = file.content_type || '';
    return type.startsWith('image/') || type === 'application/pdf' || type.startsWith('audio/');
  };
  
  // Open preview
  const openPreview = async (file) => {
    setPreviewLoading(true);
    setPreviewFile(file);
    setPreviewLoading(false);
  };
  
  // Get preview URL with auth token
  const getPreviewUrlWithAuth = (file) => {
    return tagesplanungAPI.getPreviewUrl(file.day, file.id);
  };
  
  const goToPrevDay = () => {
    if (currentDay > 1) {
      setEditMode(false);
      setEditingContent(null);
      setCurrentDay(currentDay - 1);
    }
  };
  
  const goToNextDay = () => {
    if (currentDay < 15) {
      setEditMode(false);
      setEditingContent(null);
      setCurrentDay(currentDay + 1);
    }
  };
  
  // Start editing mode
  const startEditing = () => {
    // Deep copy the current content for editing
    const contentToEdit = JSON.parse(JSON.stringify(currentPlan.zeitbloecke));
    setEditingContent(contentToEdit);
    setEditMode(true);
  };
  
  // Cancel editing
  const cancelEditing = () => {
    setEditMode(false);
    setEditingContent(null);
  };
  
  // Save edited content
  const saveContent = async () => {
    if (!editingContent) return;
    
    setSaving(true);
    try {
      await tagesplanungAPI.updateContent(currentDay, editingContent);
      await loadAllCustomContent();
      setEditMode(false);
      setEditingContent(null);
    } catch (error) {
      console.error('Error saving content:', error);
      alert('Fehler beim Speichern: ' + (error.response?.data?.detail || error.message));
    }
    setSaving(false);
  };
  
  // Reset to default content
  const resetContent = async () => {
    if (!window.confirm('Wirklich auf Standard zurücksetzen? Alle Änderungen gehen verloren.')) return;
    
    try {
      await tagesplanungAPI.resetContent(currentDay);
      await loadAllCustomContent();
      setEditMode(false);
      setEditingContent(null);
    } catch (error) {
      console.error('Error resetting content:', error);
      alert('Fehler beim Zurücksetzen: ' + (error.response?.data?.detail || error.message));
    }
  };
  
  // Update a specific block in editing mode
  const updateBlock = (blockIdx, field, value) => {
    const newContent = [...editingContent];
    newContent[blockIdx] = { ...newContent[blockIdx], [field]: value };
    setEditingContent(newContent);
  };
  
  // Update a specific detail in a block
  const updateDetail = (blockIdx, detailIdx, value) => {
    const newContent = [...editingContent];
    newContent[blockIdx].details[detailIdx] = value;
    setEditingContent(newContent);
  };
  
  // Add a new detail to a block
  const addDetail = (blockIdx) => {
    const newContent = [...editingContent];
    newContent[blockIdx].details.push('Neuer Punkt');
    setEditingContent(newContent);
  };
  
  // Remove a detail from a block
  const removeDetail = (blockIdx, detailIdx) => {
    const newContent = [...editingContent];
    newContent[blockIdx].details.splice(detailIdx, 1);
    setEditingContent(newContent);
  };
  
  // Add a new block
  const addBlock = () => {
    const newBlock = {
      zeit: 'Neue Zeit',
      thema: 'Neues Thema',
      icon: 'coffee',
      details: ['Erster Punkt']
    };
    setEditingContent([...editingContent, newBlock]);
  };
  
  // Remove a block
  const removeBlock = (blockIdx) => {
    if (editingContent.length <= 1) {
      alert('Mindestens ein Block muss vorhanden sein');
      return;
    }
    const newContent = [...editingContent];
    newContent.splice(blockIdx, 1);
    setEditingContent(newContent);
  };

  const getBlockColor = (icon) => {
    switch (icon) {
      case 'coffee': return 'bg-amber-50 border-amber-200 text-amber-800';
      case 'moon': return 'bg-indigo-50 border-indigo-200 text-indigo-800';
      case 'sun': return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'users': return 'bg-blue-50 border-blue-200 text-blue-800';
      case 'target': return 'bg-emerald-50 border-emerald-200 text-emerald-800';
      case 'lightbulb': return 'bg-purple-50 border-purple-200 text-purple-800';
      case 'check': return 'bg-green-50 border-green-200 text-green-800';
      default: return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  // Count total files for badge display
  const getFilesCount = (day) => {
    return files[day]?.length || 0;
  };

  return (
    <Dialog open={open} onOpenChange={(open) => {
      if (!open) {
        setEditMode(false);
        setEditingContent(null);
      }
      onClose(open);
    }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#4b95d1]" />
            Tagesplanung - Schulungsprogramm
            {isAdmin && (
              <Badge variant="secondary" className="ml-2 bg-[#4b95d1] text-white">
                Admin
              </Badge>
            )}
            {hasCustomContent && (
              <Badge variant="outline" className="ml-2 text-amber-600 border-amber-300">
                Angepasst
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>
        
        {/* Day Navigation */}
        <div className="flex items-center justify-between bg-gradient-to-r from-[#1c2e52] to-[#3e4350] rounded-xl p-4 text-white">
          <Button
            variant="ghost"
            size="icon"
            onClick={goToPrevDay}
            disabled={currentDay === 1 || editMode}
            className="text-white hover:bg-white/20 disabled:opacity-30"
          >
            <ChevronLeft className="w-6 h-6" />
          </Button>
          
          <div className="text-center">
            <p className="text-sm text-[#e6d7bf]">Woche {currentPlan?.woche}</p>
            <h2 className="text-2xl font-bold">
              Tag {currentDay}: {currentPlan?.name}
            </h2>
            {!editMode && (
              <div className="flex gap-1 justify-center mt-2">
                {TAGESPLANUNG.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setEditMode(false);
                      setEditingContent(null);
                      setCurrentDay(idx + 1);
                    }}
                    className={`w-2 h-2 rounded-full transition-all relative ${
                      currentDay === idx + 1 
                        ? 'bg-white w-4' 
                        : 'bg-white/30 hover:bg-white/50'
                    }`}
                  >
                    {(getFilesCount(idx + 1) > 0 || customContent[idx + 1]) && (
                      <span className={`absolute -top-1 -right-1 w-2 h-2 rounded-full ${
                        customContent[idx + 1] ? 'bg-amber-400' : 'bg-emerald-400'
                      }`} />
                    )}
                  </button>
                ))}
              </div>
            )}
            {editMode && (
              <p className="text-sm text-amber-300 mt-2">Bearbeitungsmodus aktiv</p>
            )}
          </div>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={goToNextDay}
            disabled={currentDay === 15 || editMode}
            className="text-white hover:bg-white/20 disabled:opacity-30"
          >
            <ChevronRight className="w-6 h-6" />
          </Button>
        </div>
        
        {/* Admin Edit Controls */}
        {isAdmin && (
          <div className="flex gap-2 justify-end">
            {!editMode ? (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={startEditing}
                  className="text-[#4b95d1] border-[#4b95d1] hover:bg-[#4b95d1] hover:text-white"
                >
                  <Edit3 className="w-4 h-4 mr-2" />
                  Inhalt bearbeiten
                </Button>
                {hasCustomContent && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={resetContent}
                    className="text-amber-600 border-amber-300 hover:bg-amber-50"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Zurücksetzen
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={cancelEditing}
                  className="text-gray-600"
                >
                  <X className="w-4 h-4 mr-2" />
                  Abbrechen
                </Button>
                <Button
                  size="sm"
                  onClick={saveContent}
                  disabled={saving}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Speichern
                </Button>
              </>
            )}
          </div>
        )}
        
        {/* Day Content */}
        <div className="flex-1 overflow-y-auto space-y-4 py-4 pr-2">
          {/* Files Section */}
          <Card className="border-2 border-dashed border-[#4b95d1]/30 bg-[#4b95d1]/5">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2 text-[#4b95d1]">
                  <FileText className="w-4 h-4" />
                  Materialien für Tag {currentDay}
                  {currentDayFiles.length > 0 && (
                    <Badge variant="secondary" className="ml-2">
                      {currentDayFiles.length} {currentDayFiles.length === 1 ? 'Datei' : 'Dateien'}
                    </Badge>
                  )}
                </CardTitle>
                
                {/* Admin Upload Button */}
                {isAdmin && (
                  <>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      className="hidden"
                      accept="*/*"
                    />
                    <Button
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="bg-[#4b95d1] hover:bg-[#1c2e52]"
                    >
                      {uploading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      Datei hochladen
                    </Button>
                  </>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {loadingFiles ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-[#4b95d1]" />
                </div>
              ) : currentDayFiles.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  {isAdmin 
                    ? 'Noch keine Dateien für diesen Tag. Lade eine Datei hoch!'
                    : 'Keine Materialien für diesen Tag verfügbar.'
                  }
                </p>
              ) : (
                <div className="space-y-2">
                  {currentDayFiles.map((file) => (
                    <div
                      key={file.id}
                      className="flex items-center justify-between p-3 bg-white rounded-lg border hover:shadow-sm transition-shadow"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="p-2 rounded-lg bg-gray-100">
                          {getFileIcon(file.content_type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">
                            {file.original_name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatFileSize(file.size)}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {file.has_content === false ? (
                          <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-xs" data-testid={`file-lost-${file.id}`}>
                            Datei verloren – bitte neu hochladen
                          </Badge>
                        ) : (
                          <>
                            {/* Preview Button (for images, PDFs and audio) */}
                            {canPreview(file) && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openPreview(file)}
                                className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                                title="Vorschau"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            )}
                            
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDownload(file)}
                              className="text-[#4b95d1] border-[#4b95d1] hover:bg-[#4b95d1] hover:text-white"
                              title="Herunterladen"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {isAdmin && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteFile(file.id)}
                            className="text-red-500 border-red-300 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Training Blocks - Edit Mode */}
          {editMode && editingContent ? (
            <>
              {editingContent.map((block, blockIdx) => (
                <Card key={blockIdx} className="border-l-4 border-amber-400 bg-amber-50/30">
                  <CardHeader className="pb-2">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 flex-1">
                          <select
                            value={block.icon}
                            onChange={(e) => updateBlock(blockIdx, 'icon', e.target.value)}
                            className="p-2 rounded-lg border bg-white text-sm"
                          >
                            <option value="coffee">Morgens</option>
                            <option value="moon">Abends</option>
                            <option value="sun">Wochenende</option>
                            <option value="users">Meeting</option>
                            <option value="target">Reflexion</option>
                            <option value="lightbulb">Tipp</option>
                            <option value="check">Abschluss</option>
                          </select>
                          <Input
                            value={block.zeit}
                            onChange={(e) => updateBlock(blockIdx, 'zeit', e.target.value)}
                            placeholder="Zeit"
                            className="w-32"
                          />
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeBlock(blockIdx)}
                          className="text-red-500 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <Input
                        value={block.thema}
                        onChange={(e) => updateBlock(blockIdx, 'thema', e.target.value)}
                        placeholder="Thema"
                        className="font-semibold"
                      />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {block.details.map((detail, detailIdx) => (
                        <div key={detailIdx} className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-[#4b95d1] flex-shrink-0 mt-2" />
                          <Textarea
                            value={detail}
                            onChange={(e) => updateDetail(blockIdx, detailIdx, e.target.value)}
                            className="flex-1 min-h-[40px] text-sm"
                            rows={1}
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeDetail(blockIdx, detailIdx)}
                            className="text-red-400 hover:text-red-600 p-1"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => addDetail(blockIdx)}
                        className="w-full mt-2 text-[#4b95d1] border-dashed"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Punkt hinzufügen
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              <Button
                variant="outline"
                onClick={addBlock}
                className="w-full border-dashed border-2 py-6 text-[#4b95d1]"
              >
                <Plus className="w-5 h-5 mr-2" />
                Neuen Block hinzufügen
              </Button>
            </>
          ) : (
            /* Training Blocks - View Mode */
            currentPlan?.zeitbloecke.map((block, idx) => (
              <Card 
                key={idx} 
                className={`border-l-4 ${getBlockColor(block.icon)}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${
                        block.icon === 'coffee' ? 'bg-amber-100' :
                        block.icon === 'moon' ? 'bg-indigo-100' :
                        block.icon === 'sun' ? 'bg-yellow-100' :
                        block.icon === 'users' ? 'bg-blue-100' :
                        block.icon === 'target' ? 'bg-emerald-100' :
                        block.icon === 'lightbulb' ? 'bg-purple-100' :
                        'bg-green-100'
                      }`}>
                        {getIcon(block.icon)}
                      </div>
                      <div>
                        <CardTitle className="text-base">{block.thema}</CardTitle>
                        <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                          <Clock className="w-3 h-3" />
                          {block.zeit}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {block.details.map((detail, detailIdx) => (
                      <li key={detailIdx} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-[#4b95d1] flex-shrink-0 mt-0.5" />
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))
          )}
        </div>
        
        {/* Quick Navigation */}
        {!editMode && (
          <div className="flex-shrink-0 border-t pt-4">
            <div className="flex gap-2 overflow-x-auto pb-2">
              {[1, 2].map(woche => (
                <div key={woche} className="flex gap-1 items-center">
                  <Badge variant="outline" className="whitespace-nowrap text-xs">
                    Woche {woche}
                  </Badge>
                  {TAGESPLANUNG.filter(p => p.woche === woche).map(p => (
                    <Button
                      key={p.tag}
                      variant={currentDay === p.tag ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        setEditMode(false);
                        setEditingContent(null);
                        setCurrentDay(p.tag);
                      }}
                      className={`h-8 px-3 relative ${
                        currentDay === p.tag 
                          ? 'bg-[#4b95d1] hover:bg-[#1c2e52]' 
                          : ''
                      }`}
                    >
                      {p.tag}
                      {(getFilesCount(p.tag) > 0 || customContent[p.tag]) && (
                        <span className={`absolute -top-1 -right-1 w-3 h-3 rounded-full text-[8px] text-white flex items-center justify-center ${
                          customContent[p.tag] ? 'bg-amber-500' : 'bg-emerald-500'
                        }`}>
                          {customContent[p.tag] ? '!' : getFilesCount(p.tag)}
                        </span>
                      )}
                    </Button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
      
      {/* File Preview Modal */}
      <Dialog open={!!previewFile} onOpenChange={(open) => !open && setPreviewFile(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-emerald-600" />
              Vorschau: {previewFile?.original_name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto bg-gray-100 rounded-lg p-2 min-h-[400px]">
            {previewLoading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-8 h-8 animate-spin text-[#4b95d1]" />
              </div>
            ) : previewFile && (
              <>
                {previewFile.content_type?.startsWith('image/') ? (
                  <img 
                    src={getPreviewUrlWithAuth(previewFile)}
                    alt={previewFile.original_name}
                    className="max-w-full max-h-[70vh] mx-auto object-contain"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '';
                      e.target.alt = 'Bild konnte nicht geladen werden';
                    }}
                  />
                ) : previewFile.content_type === 'application/pdf' ? (
                  <iframe
                    src={getPreviewUrlWithAuth(previewFile)}
                    className="w-full h-[70vh] border-0 rounded"
                    title={previewFile.original_name}
                  />
                ) : previewFile.content_type?.startsWith('audio/') ? (
                  <div className="flex flex-col items-center justify-center h-full gap-4">
                    <FileAudio className="w-16 h-16 text-emerald-500" />
                    <p className="font-medium text-gray-700">{previewFile.original_name}</p>
                    <audio
                      controls
                      className="w-full max-w-md"
                      src={getPreviewUrlWithAuth(previewFile)}
                      data-testid="audio-preview-player"
                    >
                      Dein Browser unterstützt keine Audio-Wiedergabe.
                    </audio>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-gray-500">
                    <File className="w-16 h-16 mb-4" />
                    <p>Vorschau für diesen Dateityp nicht verfügbar</p>
                    <Button
                      className="mt-4"
                      onClick={() => handleDownload(previewFile)}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Herunterladen
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
          
          <div className="flex justify-between items-center pt-4 border-t">
            <div className="text-sm text-gray-500">
              {formatFileSize(previewFile?.size || 0)}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  // Open in new tab
                  window.open(getPreviewUrlWithAuth(previewFile), '_blank');
                }}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                In neuem Tab öffnen
              </Button>
              <Button
                onClick={() => handleDownload(previewFile)}
                className="bg-[#4b95d1] hover:bg-[#1c2e52]"
              >
                <Download className="w-4 h-4 mr-2" />
                Herunterladen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default TagesplanungView;
